import type { PlanUpdateRequest } from '@cfa/pathway-fe-shared/xp';

import Constants from '@/constants';

import { LanguageObject } from '@/types/types';

import { store } from '@/atoms/store';
import { userPrefsAtom } from '@/atoms/userPrefs';

// Overloads preserve the caller's value type while the implementation normalizes language keys.
function processNameObjLangKeys(name?: LanguageObject): LanguageObject;
function processNameObjLangKeys<T>(name?: Record<string, T>): Record<string, T>;
function processNameObjLangKeys(name?: object) {
  if (!name) {
    return {};
  }
  const shortLangCodes = [
    Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE,
    Constants.LANGUAGE.SPANISH_LANGUAGE_CODE,
  ];

  const normalizedName: Record<string, unknown> = {};

  Object.entries(name).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    if (shortLangCodes.includes(key)) {
      normalizedName[key] = value;
      return;
    }

    const keyStart = key.split('-').at(0);
    if (keyStart && shortLangCodes.includes(keyStart)) {
      normalizedName[keyStart] = value;
      return;
    }

    normalizedName[Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE] = value;
  });

  return normalizedName;
}

/**
 * Legacy function to get a name from a language object.
 * @deprecated Use the new `itemByLanguage` function instead.
 */
export const getNameFromLanguage = (
  rawName: LanguageObject,
  selected?: string,
  strict?: boolean,
) => {
  if (typeof rawName === 'string') {
    return rawName;
  }
  const name = processNameObjLangKeys(rawName);
  const language = (() => {
    switch (true) {
      case selected === Constants.LANGUAGE_OPTIONS.ENGLISH ||
        selected === Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE: {
        if (strict) {
          return name.en;
        }
        return name.en ?? name.es;
      }
      case selected === Constants.LANGUAGE_OPTIONS.SPANISH ||
        selected === Constants.LANGUAGE.SPANISH_LANGUAGE_CODE: {
        if (strict) {
          return name.es;
        }
        // Not all names have a Spanish counterpart so we return English in those cases
        return name.es ?? name.en;
      }
      case selected === Constants.ADMIN_MANAGING_COUNTRIES.PR.LABEL_KEY:
        // Not all names have an English counterpart so we return Spanish in those cases
        return name.en ?? name.es;
      default: {
        const currentLanguage = store.get(userPrefsAtom).language;
        return currentLanguage !== Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE &&
          currentLanguage !== Constants.LANGUAGE_OPTIONS.ENGLISH &&
          name.es
          ? name.es
          : (name.en ?? name.es);
      }
    }
  })();
  if (!language) {
    return '';
  }
  return language;
};

export function itemByLanguage<T>(
  name: Record<string, T> | undefined,
  fallback: NoInfer<T>,
  selected?: string,
): T;
export function itemByLanguage<T>(
  name: Record<string, T> | undefined,
  fallback?: undefined,
  selected?: string,
): T | undefined;
export function itemByLanguage<T>(
  rawName: Record<string, T> | undefined,
  fallback?: NoInfer<T>,
  selected?: string,
): T | undefined {
  const name = processNameObjLangKeys(rawName);

  const item = (() => {
    switch (true) {
      case selected === Constants.LANGUAGE_OPTIONS.ENGLISH ||
        selected === Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE:
        return name.en ?? name.es;
      case selected === Constants.LANGUAGE_OPTIONS.SPANISH ||
        selected === Constants.LANGUAGE.SPANISH_LANGUAGE_CODE:
        // Not all names have a Spanish counterpart so we return English in those cases
        return name.es ?? name.en;
      case selected === Constants.ADMIN_MANAGING_COUNTRIES.PR.LABEL_KEY:
        // Not all names have an English counterpart so we return Spanish in those cases
        return name.en ?? name.es;
      default: {
        const currentLanguage = store.get(userPrefsAtom).language;
        return currentLanguage !== Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE &&
          name.es
          ? name.es
          : (name.en ?? name.es);
      }
    }
  })();

  return item ?? fallback;
}

export const createCurrentLanguageValue = (
  value: string,
  currentLanguage: string,
): PlanUpdateRequest['name'] => ({
  [getLanguageCode(currentLanguage)]: value,
});

export const getLanguageCode = (
  language = Constants.LANGUAGE_OPTIONS.ENGLISH,
) => {
  switch (language) {
    case Constants.LANGUAGE.SPANISH_LANGUAGE_CODE:
    case Constants.LANGUAGE_OPTIONS.ESPANOL:
    case Constants.LANGUAGE_OPTIONS.SPANISH:
      return Constants.LANGUAGE.SPANISH_LANGUAGE_CODE;
    case Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE:
    case Constants.LANGUAGE_OPTIONS.INGLES:
      return Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE;
    default:
      return Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE;
  }
};

export const pluralize = (
  quantity: number,
  singular: string,
  plural: string,
) => (quantity === 1 ? singular : plural);
